import { useEffect, useRef } from 'react'

import { F0Button, F0Text, useAiChat } from '@factorialco/f0-react'
import { Feed } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { BenchmarkMatchResult } from 'modules/job_catalog/benchmark/types'

/**
 * Approximate months since a survey year. The benchmark API only exposes the
 * survey `year` (no month/day), so this assumes the data was collected at the
 * start of that year. The result is therefore a rough estimate — e.g. a 2025
 * survey queried in January 2026 would show "12 months ago".
 *
 * Returns `null` when the survey year is current (0 months elapsed) so the
 * caller can omit the "last updated" label for recent data.
 */
const getMonthsSinceYear = (year: number | null | undefined): number | null => {
  if (year == null) return null
  const now = new Date()
  const months = (now.getFullYear() - year) * 12 + now.getMonth()
  if (months <= 0) return null
  return months
}
interface SalaryDisplay {
  amount: string
  periodicity: string
}

type BodyStatus = 'hasData' | 'noInfo' | 'suppressed'

interface Props {
  status: BodyStatus
  benchmark: BenchmarkMatchResult
  levelName: string
  roleName?: string
  familyName?: string
  functionName?: string
  workplaceName?: string
  salaryDisplay: SalaryDisplay
}

interface BuildPromptParams {
  levelName: string
  roleName?: string
  familyName?: string
  functionName?: string
  workplaceName?: string
}

export function buildBenchmarkReportPrompt({
  levelName,
  roleName,
  familyName,
  functionName,
  workplaceName,
}: BuildPromptParams): string {
  const path = [familyName, functionName, roleName].filter(Boolean).join(' > ')
  const location = workplaceName ?? ''
  if (path && location) {
    return i18n.t('job_catalog.benchmark.one_report_prompt', {
      path,
      levelName,
      workplaceName: location,
    })
  } else if (path) {
    return i18n.t('job_catalog.benchmark.one_report_prompt_no_workplace', { path, levelName })
  } else if (location) {
    return i18n.t('job_catalog.benchmark.one_report_prompt_no_path', {
      levelName,
      workplaceName: location,
    })
  }
  return i18n.t('job_catalog.benchmark.one_report_prompt_minimal', { levelName })
}

const SalaryPopoverBody = ({
  status,
  benchmark,
  levelName,
  roleName,
  familyName,
  functionName,
  workplaceName,
  salaryDisplay,
}: Props) => {
  const aiChat = useAiChat()
  const sendTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (sendTimerRef.current) clearTimeout(sendTimerRef.current)
    }
  }, [])

  const handleViewReport = () => {
    if (!aiChat.enabled) return
    const prompt = buildBenchmarkReportPrompt({
      levelName,
      roleName,
      familyName,
      functionName,
      workplaceName,
    })
    if (sendTimerRef.current) clearTimeout(sendTimerRef.current)
    aiChat.clear()
    aiChat.setOpen(true)
    sendTimerRef.current = setTimeout(() => {
      aiChat.sendMessage(prompt)
    }, 150)
  }

  if (status === 'noInfo' || status === 'suppressed') {
    const description =
      status === 'noInfo'
        ? i18n.t('job_catalog.benchmark.states.no_data.description')
        : i18n.t('job_catalog.benchmark.states.suppressed.description')

    return (
      // eslint-disable-next-line factorial/no-raw-html -- exact grouped layout for empty benchmark state not achievable via F0Box
      <div
        style={{
          boxSizing: 'border-box',
          width: 392,
          height: 156,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          background: 'white',
          padding: '8px 0 12px',
          borderRadius: 12,
        }}
      >
        {/* eslint-disable-next-line factorial/no-raw-html -- exact dimensions and padding for empty state content block not achievable via F0Box */}
        <div
          style={{
            boxSizing: 'border-box',
            width: 392,
            height: 84,
            display: 'flex',
            flexDirection: 'row',
            gap: 8,
            padding: '12px 16px 12px 12px',
            borderRadius: 12,
          }}
        >
          {/* eslint-disable-next-line factorial/no-raw-html -- wrapper div for icon sizing */}
          <div>
            <Feed color="hsl(var(--neutral-solid-40))" width={32} height={32} />
          </div>
          {/* eslint-disable-next-line factorial/no-raw-html -- wrapper div for text */}
          <div>
            <F0Text variant="body" content={description} />
          </div>
        </div>

        {/* eslint-disable-next-line factorial/no-raw-html -- exact empty state footer layout not achievable via F0Box */}
        <div
          style={{
            boxSizing: 'border-box',
            width: 392,
            height: 56,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 8,
            padding: '0 16px',
          }}
        >
          <div
            style={{
              width: 102,
              height: 32,
              minWidth: 102,
              flex: '0 0 102px',
              flexShrink: 0,
              borderRadius: 10,
            }}
          >
            <F0Button
              label={i18n.t('job_catalog.benchmark.view_report')}
              variant="neutral"
              size="sm"
              disabled={!aiChat.enabled}
              onClick={handleViewReport}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    // eslint-disable-next-line factorial/no-raw-html -- exact border, padding, and box-sizing not achievable via F0Box tokens
    <div
      style={{
        boxSizing: 'border-box',
        width: 392,
        height: 156,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 8,
        padding: '8px 16px 12px',
        borderRadius: 12,
        background: 'white',
      }}
    >
      {/* eslint-disable-next-line factorial/no-raw-html -- exact dimensions and spacing for salary summary block not achievable via F0Box */}
      <div
        style={{
          boxSizing: 'border-box',
          width: 360,
          height: 96,
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          paddingTop: 8,
          paddingBottom: 8,
          borderRadius: 12,
        }}
      >
        {/* eslint-disable-next-line factorial/no-raw-html -- exact grouping for label and salary row not achievable via F0Box */}
        <div style={{ width: 360, height: 56, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <F0Text
            variant="description"
            content={i18n.t('job_catalog.benchmark.market_salary_range')}
          />

          {/* eslint-disable-next-line factorial/no-raw-html -- baseline alignment for amount + periodicity not achievable via F0Box */}
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 4 }}>
            {/* eslint-disable-next-line factorial/no-raw-html -- h3 with custom font-size/weight/letter-spacing not available via F0Text */}
            <h3
              style={{
                margin: 0,
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: 22,
                lineHeight: '125%',
                letterSpacing: '0',
                color: 'hsl(var(--neutral-100))',
              }}
            >
              {salaryDisplay.amount}
            </h3>
            {/* eslint-disable-next-line factorial/no-raw-html -- span with custom font styling not available via F0Text */}
            <span
              style={{
                color: 'hsl(var(--neutral-100))',
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: 14,
                lineHeight: '145%',
                letterSpacing: '0',
              }}
            >
              {salaryDisplay.periodicity}
            </span>
          </div>
        </div>

        <F0Text
          variant="description"
          content={
            roleName
              ? workplaceName
                ? i18n.t('job_catalog.benchmark.market_salary_for_role_in_workplace', {
                    roleName,
                    levelName,
                    workplaceName,
                  })
                : i18n.t('job_catalog.benchmark.market_salary_for_role', {
                    roleName,
                    levelName,
                  })
              : i18n.t('job_catalog.benchmark.market_salary_for_level', { levelName })
          }
        />
      </div>

      {/* eslint-disable-next-line factorial/no-raw-html -- space-between row for metadata + button not achievable via F0Box */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        {(benchmark.orgCount != null || benchmark.year != null) && (
          // eslint-disable-next-line factorial/no-raw-html -- flex: 1 + minWidth: 0 for text truncation not achievable via F0Box
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* eslint-disable-next-line factorial/no-raw-html -- tertiary metadata color isn't exposed through F0Text props */}
            <div className="text-f1-foreground-tertiary">
              <F0Text
                variant="small"
                content={[
                  benchmark.orgCount != null
                    ? i18n.tp('job_catalog.benchmark.based_on_data', {
                        count: benchmark.orgCount,
                      })
                    : null,
                  (() => {
                    const months = getMonthsSinceYear(benchmark.year)
                    if (months == null) return null
                    return i18n.tp('job_catalog.benchmark.last_updated', { count: months })
                  })(),
                ]
                  .filter(Boolean)
                  .join(' ')}
              />
            </div>
          </div>
        )}
        {/* eslint-disable-next-line factorial/no-raw-html -- exact flex-shrink: 0 + fixed width for button not achievable via F0Box */}
        <div style={{ width: 102, minWidth: 102, flex: '0 0 102px', flexShrink: 0 }}>
          <F0Button
            label={i18n.t('job_catalog.benchmark.view_report')}
            variant="neutral"
            size="sm"
            disabled={!aiChat.enabled}
            onClick={handleViewReport}
          />
        </div>
      </div>
    </div>
  )
}

export default SalaryPopoverBody
